import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

import { getApiBaseUrl } from '../../utils/env'

const REFRESH_TOKEN_KEY = 'hikestrong-dashboard-refresh'
const STORAGE_KEY = 'hikestrong-dashboard-auth'

const isCredentialRequest = (url?: string) =>
  ['/api/auth/login', '/api/auth/google', '/api/auth/refresh', '/api/auth/logout', '/api/auth/reset-password']
    .some(path => url?.includes(path))

export const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true
})

let isRefreshing = false
let failedQueue: Array<{
  resolve: (value: unknown) => void
  reject: (error: unknown) => void
}> = []

// Only a definitive rejection means the refresh token is dead; a network error or 5xx is
// temporary and must not discard a still-valid remembered session (BH-17).
const isRefreshTokenRejected = (error: unknown) => {
  const status = (error as AxiosError | undefined)?.response?.status
  return status === 400 || status === 401 || status === 403
}

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Don't retry refresh requests or already retried requests
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/api/auth/refresh') &&
      !originalRequest.url?.includes('/api/auth/login') &&
      !originalRequest.url?.includes('/api/auth/google')
    ) {
      if (isRefreshing) {
        // Wait for the current refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return apiClient(originalRequest)
        }).catch((err) => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      const refreshToken = window.localStorage.getItem(REFRESH_TOKEN_KEY)

      if (refreshToken) {
        try {
          const response = await apiClient.post('/api/auth/refresh', { refreshToken })
          const { token: newToken, refreshToken: newRefreshToken, user } = response.data

          // Update stored tokens
          apiClient.defaults.headers.common.Authorization = `Bearer ${newToken}`
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
            token: newToken,
            user,
            expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString()
          }))
          if (newRefreshToken) {
            window.localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken)
          }

          processQueue(null, newToken)

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return apiClient(originalRequest)
        } catch (refreshError) {
          processQueue(refreshError, null)

          if (!isRefreshTokenRejected(refreshError)) {
            return Promise.reject(refreshError)
          }

          // Clear auth state - user needs to log in again
          window.localStorage.removeItem(STORAGE_KEY)
          window.localStorage.removeItem(REFRESH_TOKEN_KEY)
          delete apiClient.defaults.headers.common.Authorization

          // Redirect to login
          if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
            window.location.href = '/login'
          }

          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      } else {
        // No refresh token - redirect to login
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          window.location.href = '/login'
        }
      }
    }

    if (import.meta.env.DEV && !isCredentialRequest(originalRequest?.url)) {
      console.error('API error', error)
    }
    return Promise.reject(error)
  }
)
